import React, { useState, useEffect } from 'react';
import {
  Container, Typography, TextField, List, ListItemButton,
  ListItemText, CircularProgress, Alert, Button, Box, IconButton,
  Avatar, ListItemAvatar
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { apiFetch } from '../apiFetch';
import { Grocery } from '../types/Grocery';
import GroceryFormModal from '../modals/GroceryFormModal';
import DeleteModal from '../modals/DeleteModal';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useTutorial } from '../hooks/useTutorial';

const Groceries: React.FC = () => {
  const [groceries, setGroceries] = useState<Grocery[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Modal state
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingGrocery, setEditingGrocery] = useState<Grocery | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingGrocery, setDeletingGrocery] = useState<Grocery | null>(null);

  // Usage check state
  const [usageLoading, setUsageLoading] = useState(false);
  const [groceryUsage, setGroceryUsage] = useState<{
    usedInLists: { id: number; name: string }[];
    hasMore: boolean;
  }>({ usedInLists: [], hasMore: false });

  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_API_URL;

  useTutorial('groceries', { hasData: groceries.length > 0 });

  useEffect(() => {
    const fetchGroceries = async () => {
      try {
        const data = await apiFetch(`${apiUrl}/api/groceries/`);
        setGroceries(data);
      } catch (err: any) {
        setError(err.message || 'An error occurred while fetching groceries.');
      } finally {
        setLoading(false);
      }
    };
    fetchGroceries();
  }, [apiUrl]);

  // Filter groceries based on the search term
  const filteredGroceries = groceries
    .filter((grocery) => {
      const lowerSearch = searchTerm.toLowerCase();
      return (
        grocery.name.toLowerCase().includes(lowerSearch) ||
        (grocery.brand && grocery.brand.toLowerCase().includes(lowerSearch)) ||
        grocery.category.toLowerCase().includes(lowerSearch) ||
        (grocery.barcode && grocery.barcode.toLowerCase().includes(lowerSearch))
      );
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  // Handle opening the Add modal
  const handleAddClick = () => {
    setEditingGrocery(null);
    setFormModalOpen(true);
  };

  // Handle opening the Edit modal
  const handleEditClick = (e: React.MouseEvent, grocery: Grocery) => {
    e.preventDefault();
    e.stopPropagation();
    setEditingGrocery(grocery);
    setFormModalOpen(true);
  };

  // Handle opening the Delete modal with usage check
  const handleDeleteClick = async (e: React.MouseEvent, grocery: Grocery) => {
    e.preventDefault();
    e.stopPropagation();
    setDeletingGrocery(grocery);
    setUsageLoading(true);
    setDeleteModalOpen(true);

    try {
      const usage = await apiFetch(`${apiUrl}/api/groceries/${grocery.id}/usage/`);
      setGroceryUsage({
        usedInLists: usage.open_lists || [],
        hasMore: usage.has_more || false,
      });
    } catch (err) {
      // If usage check fails, just show basic delete modal
      setGroceryUsage({ usedInLists: [], hasMore: false });
    } finally {
      setUsageLoading(false);
    }
  };

  // Handle closing the Delete modal
  const handleDeleteModalClose = () => {
    setDeleteModalOpen(false);
    setDeletingGrocery(null);
    setGroceryUsage({ usedInLists: [], hasMore: false });
  };

  // Handle successful form submission (add or edit)
  const handleFormSuccess = (savedGrocery: Grocery) => {
    if (editingGrocery) {
      setGroceries((prev) =>
        prev.map((g) => (g.id === savedGrocery.id ? savedGrocery : g))
      );
      setSuccessMessage('Grocery updated successfully.');
    } else {
      setGroceries((prev) => [...prev, savedGrocery]);
      setSuccessMessage('Grocery added successfully.');
    }
    setTimeout(() => setSuccessMessage(null), 4000);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!deletingGrocery) return;
    try {
      await apiFetch(`${apiUrl}/api/groceries/${deletingGrocery.id}/`, {
        method: 'DELETE',
      });
      setGroceries((prev) => prev.filter((g) => g.id !== deletingGrocery.id));
      setSuccessMessage('Grocery deleted successfully.');
      setTimeout(() => setSuccessMessage(null), 4000);
    } catch (err: any) {
      setError(err.message || 'An error occurred while deleting the grocery.');
    } finally {
      handleDeleteModalClose();
    }
  };

  // Format secondary text for list item
  const formatSecondaryText = (grocery: Grocery): string => {
    const parts = [];
    if (grocery.brand) parts.push(grocery.brand);
    if (grocery.category) parts.push(grocery.category);
    if (grocery.packaging_size) parts.push(grocery.packaging_size);
    return parts.join(' | ');
  };

  return (
    <Container maxWidth="md" sx={{ mt: 5 }}>
      <Box className="page-header">
        <Box className="title-row">
          <IconButton onClick={() => navigate('/')} aria-label="back to home">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1" className="page-title">Manage Your Groceries</Typography>
        </Box>
        <Box className="page-header-buttons">
          <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleAddClick} data-tutorial="groceries-add-button">
            Add Grocery
          </Button>
        </Box>
      </Box>
      <Typography variant="body1" gutterBottom>
        Here you can manage the groceries in your database. Add new items, update their details, and organize them by category or brand.
      </Typography>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Search bar to filter groceries */}
      <TextField
        label="Search Groceries"
        variant="outlined"
        fullWidth
        margin="normal"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        data-tutorial="groceries-search"
      />

      {loading && <CircularProgress sx={{ display: 'block', margin: '20px auto' }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <List data-tutorial="groceries-list">
        {filteredGroceries.map((grocery) => (
          <ListItemButton
            key={grocery.id}
            component={RouterLink}
            to={`/groceries/${grocery.id}`}
            sx={{ display: 'flex', justifyContent: 'space-between' }}
          >
            <ListItemAvatar>
              <Avatar
                src={grocery.image_icon || `${process.env.REACT_APP_IMAGES_URL}/placeholder-grocery.svg`}
                variant="rounded"
                sx={{ width: 40, height: 40 }}
              />
            </ListItemAvatar>
            <ListItemText
              primary={grocery.name}
              secondary={formatSecondaryText(grocery)}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton
                size="small"
                onClick={(e) => handleEditClick(e, grocery)}
                aria-label="edit grocery"
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={(e) => handleDeleteClick(e, grocery)}
                aria-label="delete grocery"
                color="error"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          </ListItemButton>
        ))}
      </List>

      {!loading && groceries.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No groceries yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Click the "Add Grocery" button above to add your first grocery item.
          </Typography>
        </Box>
      )}

      {/* Add/Edit Grocery Modal */}
      <GroceryFormModal
        open={formModalOpen}
        onClose={() => setFormModalOpen(false)}
        onSuccess={handleFormSuccess}
        grocery={editingGrocery}
      />

      {/* Delete Confirmation Modal */}
      <DeleteModal
        open={deleteModalOpen}
        onClose={handleDeleteModalClose}
        onDelete={handleDeleteConfirm}
        itemName={deletingGrocery?.name || ''}
        loading={usageLoading}
        usedInLists={groceryUsage.usedInLists}
        hasMoreLists={groceryUsage.hasMore}
      />
    </Container>
  );
};

export default Groceries;
